import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getCoachingEvidenceTimeline, recordCoachingFollowup } from '@/lib/api';
import { useScopedKey } from '@/lib/queryClient';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { ownedRequest } from '@/features/coach/assist/useAssistContext';
import type { CoachingEvidenceTimeline, RecordCoachingFollowupRequest } from '@/lib/types';

class TimelineContextMismatchError extends Error {
  constructor() { super('Coaching evidence belongs to a different player or season.'); this.name = 'TimelineContextMismatchError'; }
}
function belongsToOwner(data: CoachingEvidenceTimeline | undefined, playerId: string, owner: DraftContext): data is CoachingEvidenceTimeline {
  return !!data && data.player_id === playerId && data.season_id === owner.seasonId;
}

export function useCoachingFollowups(owner: DraftContext, playerId: string) {
  const client = useQueryClient();
  const [instance] = useState(() => crypto.randomUUID());
  const key = useScopedKey('coaching-evidence-timeline', { playerId, author: owner.actorId, generation: owner.generation, instance });
  const scope = { teamId: owner.teamId, seasonId: owner.seasonId };
  const alive = useRef(true);
  const locked = useRef(false);
  const pending = useRef<RecordCoachingFollowupRequest | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [denied, setDenied] = useState(false);
  const [conflictVersion, setConflictVersion] = useState(0);
  const [message, setMessage] = useState('');
  const current = () => alive.current && isCurrentDraftContext(owner);
  const timeline = useQuery({ queryKey: key, enabled: !denied,
    queryFn: ({ signal }) => ownedRequest(owner, async () => {
      const data = await getCoachingEvidenceTimeline(playerId, scope, signal);
      if (!belongsToOwner(data, playerId, owner)) throw new TimelineContextMismatchError();
      return data;
    }),
    retry: false, staleTime: 0, refetchOnWindowFocus: true,
  });
  useEffect(() => {
    const error = timeline.error;
    if (error instanceof TimelineContextMismatchError && current()) {
      pending.current = null; setUncertain(false); setDenied(true);
    }
    if (error instanceof ApiError && [401, 403, 404].includes(error.status) && current()) {
      pending.current = null; setUncertain(false); setDenied(true);
    }
  }, [timeline.error]);
  const operation = useMutation({ retry: false, meta: { composerContext: owner },
    mutationFn: async (body: RecordCoachingFollowupRequest) => {
      // The event is refreshed through the owned query. Do not retain a second
      // private projection in the shared mutation cache after this view closes.
      await ownedRequest(owner, () => recordCoachingFollowup(playerId, body, scope));
    },
    onSuccess: (_data, body) => {
      if (!current() || pending.current !== body) return;
      pending.current = null; setUncertain(false); setMessage('Follow-up recorded. Goal progress is unchanged.');
      void timeline.refetch();
    },
    onError: (error, body) => {
      if (!current() || pending.current !== body) return;
      if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        pending.current = null; setUncertain(false); setDenied(true); setMessage('This coaching evidence is unavailable.');
      } else if (error instanceof ApiError && [409, 422].includes(error.status) && !(typeof error.detail === 'object' && error.detail !== null && 'code' in error.detail && error.detail.code === 'evidence_retry_required')) {
        pending.current = null; setUncertain(false); setConflictVersion(value => value + 1);
        setMessage('The source or selection changed. Refresh and choose current evidence before saving.'); void timeline.refetch();
      } else {
        setUncertain(true); setMessage('Confirmation was not received. Retry the same follow-up to reconcile it.');
      }
    },
    onSettled: () => { locked.current = false; },
  });
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false; pending.current = null;
      void client.cancelQueries({ queryKey: key, exact: true }); client.removeQueries({ queryKey: key, exact: true });
    };
  }, [client, JSON.stringify(key)]);
  useEffect(() => {
    if (denied) { void client.cancelQueries({ queryKey: key, exact: true }); client.removeQueries({ queryKey: key, exact: true }); }
  }, [denied, client, JSON.stringify(key)]);
  const send = (body?: RecordCoachingFollowupRequest) => {
    if (!current() || locked.current || denied || timeline.isError || !belongsToOwner(timeline.data, playerId, owner) || (body && timeline.isFetching) || (!body && !pending.current)) return;
    if (!pending.current && body) pending.current = Object.freeze({ ...body });
    locked.current = true; setMessage(''); operation.mutate(pending.current!);
  };
  return { timeline, operation, send, uncertain, denied, conflictVersion, message,
    data: current() && !denied && !timeline.isError && belongsToOwner(timeline.data, playerId, owner) ? timeline.data : undefined };
}
